// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A reusable autocomplete component for selecting event configurations.
 * This module provides a base autocomplete interface that can be used
 * across different event selection scenarios. It extends Material-UI's
 * Autocomplete component with event-specific functionality.
 *
 * Key features:
 * - Type-safe event selection
 * - Form integration
 * - Error handling
 * - Custom option rendering
 * - Ref forwarding
 * - Accessibility support
 */
import { EventConfiguration } from '@/asset/api/types';
import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteRenderInputParams,
  TextField,
} from '@mui/material';
import { ForwardedRef, forwardRef } from 'react';

/**
 * Type definition for event options in the autocomplete.
 * Extracts only the 'type' field from EventConfiguration
 * to ensure consistent option handling.
 */
type EventOption = Pick<EventConfiguration, 'type'>;

/**
 * Compares two event options for equality.
 * Used by the autocomplete to determine if an option
 * matches the current value.
 *
 * @param option - The option to compare
 * @param value - The current value to compare against
 * @returns True if the options have matching type IDs
 *
 * @example
 * ```ts
 * const equal = isOptionEqualToValue(
 *   { type: { id: '123', name: 'Event A' } },
 *   { type: { id: '123', name: 'Event A' } }
 * ); // true
 * ```
 */
function isOptionEqualToValue(option: EventOption, value: EventOption) {
  return option.type.id === value.type.id;
}

/**
 * Extracts the display label from an event option.
 * Used by the autocomplete to show the option in the dropdown
 * and input field.
 *
 * @param option - The event option to get the label from
 * @returns The name of the event type
 *
 * @example
 * ```ts
 * const label = getOptionLabel({
 *   type: { id: '123', name: 'Maintenance Check' }
 * }); // 'Maintenance Check'
 * ```
 */
function getOptionLabel(option: EventOption) {
  return option.type.name;
}

/**
 * Props for configuring the EventAutocomplete component.
 * Extends Material-UI's AutocompleteProps with additional
 * form-specific properties.
 *
 * @template Option - The type of event option, must extend EventOption
 */
interface Props<Option extends EventOption>
  extends Omit<
    AutocompleteProps<Option, false, false, false>,
    'isOptionEqualToValue' | 'getOptionLabel' | 'renderInput'
  > {
  /**
   * The name of the form input element.
   * Used for form integration and accessibility.
   *
   * @example "eventType"
   */
  name?: string;

  /**
   * The label displayed above the input.
   * Provides context for the field's purpose.
   *
   * @example "Select event type"
   */
  label?: string;

  /**
   * Whether the component is in an error state.
   * When true, displays red border and error styling.
   *
   * @default false
   */
  error?: boolean;

  /**
   * Text displayed below the input.
   * Used for additional guidance or error messages.
   *
   * @example "Please select a valid event type"
   */
  helperText?: string;

  /**
   * Text displayed in the empty input.
   * Provides guidance on expected input format.
   *
   * @example "Search event types..."
   */
  placeholder?: string;
}

/**
 * Internal renderer for the event autocomplete component.
 * Handles the integration between Material-UI's Autocomplete
 * and TextField components while forwarding refs.
 *
 * Features:
 * - Custom input rendering
 * - Error state handling
 * - Helper text display
 * - Ref forwarding to input
 * - Form field integration
 *
 * @param props - Component configuration options
 * @param ref - Forwarded ref for the input element
 */
function EventAutocompleteRenderer<Option extends EventOption>(
  props: Props<Option>,
  ref: ForwardedRef<HTMLInputElement>
) {
  const { name, label, placeholder, error, helperText, ...autocompleteProps } =
    props;

  const renderInput = (parameters: AutocompleteRenderInputParams) => {
    return (
      <TextField
        inputRef={ref}
        name={name}
        label={label}
        error={error}
        helperText={helperText}
        placeholder={placeholder}
        {...parameters}
      />
    );
  };

  return (
    <Autocomplete
      {...autocompleteProps}
      renderInput={renderInput}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
    />
  );
}

/**
 * A controlled component for selecting events from a dropdown.
 * Provides a searchable interface with keyboard navigation
 * and form integration capabilities.
 *
 * Features:
 * - Type-safe event selection
 * - Form integration
 * - Error handling
 * - Custom option rendering
 * - Keyboard navigation
 * - Screen reader support
 *
 * Usage example:
 * ```tsx
 * const [selected, setSelected] = useState<EventConfiguration | null>(null);
 *
 * return (
 *   <EventAutocomplete
 *     label="Select event"
 *     options={eventOptions}
 *     value={selected}
 *     onChange={(_, newValue) => setSelected(newValue)}
 *     helperText="Choose an event from the list"
 *   />
 * );
 * ```
 */
export const EventAutocomplete = forwardRef(EventAutocompleteRenderer);
